import { useEffect, useState } from 'react';
import Page from '../../../components/PageWrapper';
import { Formik } from 'formik';
import { getAccountProfile, shareReferral, updateStaffProfile } from '../../../api/requests';
import FileUpload from '../../../components/elements/FileUpload/FileUpload';
import Button from '../../../components/form-control/Button';
import Input from '../../../components/form-control/Input';
import useImage from '../../../hooks/imageHook';
import useUserAccount from '../../../hooks/userAccountHook';
import { UPLOADABLE_DOCUMENT_TYPES, UPLOAD_IMAGE_ENDPOINTS } from '../../../resources/enums';
import * as Yup from 'yup';
import useModal from '../../../hooks/modalHook';
import { getFormattedPhoneNumber, getPhoneNumberFromShortCodeFormat, sentenceCase, showToast } from '../../../utils/miscFunctions';
import Loader from '../../../components/Loader';
import SelectMenu, { ISelectMenuItemValue } from '../../../components/form-control/SelectMenu';
import { Link } from 'react-router-dom';
import { REGIONS_BY_COUNTRY } from '../../../resources/constants';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../states/slices/resourceSlice';
import { Save } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface IreferralDetails {
  referralLink: string;
  referralCode: string;
}
function ProfileDetails() {
  const { setProfile, updateProfile } = useUserAccount();
  const [loading, setLoading] = useState<boolean>(false);
  const [profileData, setProfileData] = useState<any>();
  const [profileImage, setProfileImage] = useState<any>();
  const [referralDetails, setReferralDetails] = useState<IreferralDetails>();
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const { uploadStaffImage } = useImage();
  const { user } = useUserAccount()
  const selectedCountry = useSelector(getSelectedCountry);
  const queryClient = useQueryClient()

  const uploadStaffPhoto = () => {
    uploadStaffImage(
      profileImage,
      UPLOADABLE_DOCUMENT_TYPES.PASSPORT_PHOTO,
      UPLOAD_IMAGE_ENDPOINTS.STAFF
    )
      .then((response: any) => {
        queryClient.invalidateQueries({ queryKey: ['getProfileDetails'] })
      })
      .catch(() => { });
  };

  const { isLoading } = useQuery({
    queryKey: ['getProfileDetails'],
    queryFn: async () => {
      try {
        const res: any = await getAccountProfile()
        if (res) {
          setProfile(res.data);
          updateProfile(res?.data);
          setProfileData(res.data);
        }

      } catch (error: any) {
        showToast.error(error?.description ?? 'Could not load your profile. Please try again.');
      }
    }
  })

  useQuery({
    queryKey: ['generateReferralLink'],
    queryFn: async () => {
      try {
        const response = await shareReferral();
        if (response) setReferralDetails(response);
      } catch (error) { }
    }
  })




  return (
    <Page title="Profile" className="">
      <div className="min-h-full bg-white dark:bg-darkBg w-full p-5">
        {isLoading ? (
          <Loader message="Fetching account details. Please wait..." brollyLoaderFixed />
        ) : (
          <>
            {/* FORM SECTION */}
            <div className="h-full grid grid-cols-1 gap-5 ">
              <div className="col-span-1">
                {profileData && referralDetails && (
                  <>
                    <FileUpload
                      onFileLoad={(images: any) => {
                        if (images && images[0]) {
                          setProfileImage(images[0]);
                        }
                      }}
                      title="Upload photo"
                      onFileDeleted={() => {
                        setProfileImage({
                          profileImage: null
                        });
                      }}
                      type="image"
                      showFileMax={false}
                      savedImage={profileData?.image}
                      profilePhoto
                      isProfile
                    />
                  </>
                )}
              </div>

              <div className="md:col-span-3">
                {profileData && referralDetails && (
                  <Formik
                    initialValues={{
                      firstname: profileData.firstName,
                      lastname: profileData.lastName,
                      email: profileData.email,
                      phone: getPhoneNumberFromShortCodeFormat(profileData.phone),
                      password: '',
                      slackId: profileData?.slackId,
                      slackWorkspace: profileData?.slackWorkspace?.name,
                      jobTitle: profileData.jobTitle,
                      location: ''
                    }}
                    validateOnBlur
                    validationSchema={Yup.object().shape({
                      firstname: Yup.string()
                        .min(2, 'First name must be at least 2 characters.')
                        .max(50, 'First name cannot exceed 50 characters.')
                        .required('Please enter your first name.'),
                      lastname: Yup.string()
                        .min(2, 'Last name must be at least 2 characters.')
                        .max(50, 'Last name cannot exceed 50 characters.')
                        .required('Please enter your last name.'),
                      email: Yup.string().email('Please enter a valid email address.').required('Please enter your email.')
                    })}
                    onSubmit={(values) => {
                      const personalInformationPayload = {
                        firstname: values.firstname,
                        lastname: values.lastname,
                        email: values.email,
                        phone: getFormattedPhoneNumber(values.phone),
                        slackId: values.slackId,
                        jobTitle: values.jobTitle
                      };

                      let myId = profileData?.id;
                      if (myId)
                        updateStaffProfile(myId, personalInformationPayload)
                          .then((response: any) => {
                            showToast.success(response.message);
                            profileImage && uploadStaffPhoto();
                            setLoading(false);
                          })
                          .catch((error: any) => {
                            setLoading(false);
                            showToast.error(error?.description ?? 'Could not update your profile. Please try again.');
                          });
                    }}>
                    {({ handleBlur, handleChange, handleSubmit, setFieldValue, values }) => (
                      <form onSubmit={handleSubmit}>



                        <div className="space-y-6">

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input
                              type="text"
                              name="firstname"
                              label="First Name"
                              value={values.firstname}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              // error={touched.firstname ? errors.firstname : undefined}
                              // helperText={touched.firstname ? errors.firstname : undefined}
                              required
                            />
                            <Input
                              type="text"
                              name="lastname"
                              label="Last Name"
                              value={values.lastname}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              required
                            // error={touched.lastname ? errors.lastname : undefined}
                            // helperText={touched.lastname ? errors.lastname : undefined}
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Input
                              type="email"
                              name="email"
                              label="Email Address"
                              value={values.email.trim()}
                              onChange={handleChange}
                              required
                            // error={touched.email ? errors.email : undefined}
                            // helperText={touched.email ? errors.email : undefined}
                            />
                            <Input
                              type="tel"
                              name="phone"
                              label="Phone Number"
                              value={values.phone}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              required
                            />
                          </div>

                          <div className="grid grid-cols-1 ">
                            <Input
                              type="text"
                              name="jobTitle"
                              label="Job Title"
                              value={values.jobTitle}
                              onBlur={handleBlur}
                              onChange={handleChange}
                            />
                            {/* <SelectMenu
                              label="Location"
                              name="location"
                              listSelectedValue={sentenceCase(values?.location ?? '')}
                              onChange={(val: ISelectMenuItemValue | null) => {
                                setFieldValue('location', val);
                              }}
                              onBlur={handleBlur}
                              search
                              searchPlaceHolder="Search by location ..."
                              list={REGIONS_BY_COUNTRY[selectedCountry.iso]}
                              placeholder="Select region"
                              // error={touched.location ? errors.location : undefined}
                              // helperText={touched?.location ? errors.location : undefined}
                              required
                            /> */}
                          </div>

                          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                            {profileData?.slackWorkspace && (
                              <Input
                                type="text"
                                name="slackWorkspace"
                                label="Slack Workspace"
                                value={values.slackWorkspace}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                disabled
                              />
                            )}

                            {user.isInternalAdmin() && (
                              <div className="relative">
                                <Input
                                  type="text"
                                  name="slackId"
                                  label={`Slack ID`}
                                  value={values.slackId}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                />
                                <Link
                                  className="text-[10px] text-blue-700 hover:underline underline-offset-2 absolute top-1 right-0"
                                  to={`https://www.workast.com/help/article/how-to-find-a-slack-user-id/`}
                                  target="_blank"
                                  rel="noopener noreferrer">
                                  Click to find your slack ID
                                </Link>
                              </div>
                            )}
                          </div>

                        </div>


                        <div className="py-4 border-gray-200 dark:border-darkBg-border flex items-end justify-end">

                          <div className="flex items-center space-x-3">

                            <Button
                              label=" Save"
                              type="submit"
                              className="inline-flex hover:text-white items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full text-white bg-gray-800 "
                              size="lg"
                              curved
                              disabled={loading}
                              loading={loading}
                              icon={<Save className="mr-2 h-4 w-4" />}
                            />

                          </div>
                        </div>
                      </form>
                    )}
                  </Formik>
                )}
                {/* {!user.isInsurer() && ( */}
                {/* <div className="my-10">
                  <h1 className="text-xl font-semibold mb-4">Referral Details</h1>
                  {referralDetails && profileData && (
                    <div>
                      <div className="flex items-center w-full">
                        <div className="mr-4 border-r-[1px] border-r-gray-300 w-[8rem]">
                          Referral Link
                        </div>
                        <div className="flex items-center">
                          <span className="w-[15rem] overflow-ellipsis overflow-hidden mr-4 whitespace-nowrap">
                            {referralDetails.referralLink}
                          </span>
                          <span className="ml-4">
                            <ShareComponent
                              withShareBtn={true}
                              url={referralDetails.referralLink}
                              title={'Brolly referal link'}
                              text={referralDetails.referralCode}
                              bgColor={profileData?.company?.themeColour}
                              fontColor={profileData?.company?.fontColour}
                            />
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center w-full mt-4">
                        <div className="mr-4 border-r-[1px] border-r-gray-300 w-[8rem]">
                          Referral Code
                        </div>
                        <div className="flex items-center">
                          <span className="w-[15rem] overflow-ellipsis overflow-hidden mr-4 whitespace-nowrap">
                            {referralDetails.referralCode}
                          </span>
                          <Button
                            className="border-[1px] bg-[#8CEB8C] border-[#8CEB8C] rounded-[8px] w-fit h-[30px] !text-[12px] text-dark p-0 py-0"
                            label="Copy"
                            onClick={() => {
                              copyToClipboard(referralDetails.referralCode);
                              showToast.success('URL copied to clipboard');
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div> */}
                {/* )} */}
              </div>
            </div>
          </>
        )}

        <ModalComponent />
      </div>
    </Page>
  );
}

export default ProfileDetails;
